import React, { useState } from "react";
import { observer } from "mobx-react-lite"; // Import observer
import { colors } from "../../theme";
import { Stack } from "../Stack";
import { Button, Box } from "@chakra-ui/react";
import { bookingFlowStore } from '../../store/BookingFlowStore';
import { servicebookingFlowStore } from "../../store/ServiceBookingFlowStore";
import { useBookAppointmentStore } from "../../store";

export type TimePickerDateTime = {
  date: string;
  formattedDate: string;
  time?: string;
  formattedTime?: string;
  duration?: string;
};

export interface TimePickerContentProps {
  times?: TimePickerDateTime[][];
  onChange: (obj: TimePickerDateTime) => void;
  value: TimePickerDateTime;
}

// Wrap the component with observer
export const TimePickerContent = observer((props: TimePickerContentProps) => {
  const { times = [], value, onChange } = props;
  const [showAll, setShowAll] = useState(false);

  // Access MobX state within the component body
  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  const buttonBgColor = isServiceBooking && servicebookingFlowStore.configurationDetails?.colorScheme?.buttonBg 
    ? servicebookingFlowStore.configurationDetails.colorScheme.buttonBg 
    : "#B92F2F";

  // Define styles inside the component for reactivity
  const getTimeContainerStyles = (item: TimePickerDateTime) => {
    return {
      ...$timeContainerStyle,
      ...(value.date === item.date &&
        value.time === item.time && { backgroundColor: colors.primary }),
    };
  };

  const getTimesStyle = (): React.CSSProperties => ({
    gap: 0,
    borderColor: buttonBgColor,
    alignItems: "center",
    flex: 1,
  });

  // eslint-disable-next-line
  const [selectedTime, setSelectedTime] = useState<TimePickerDateTime | null>(
    null
  );

  // eslint-disable-next-line
  const [patient, doctor, serviceType, appointmentType, setData] = useBookAppointmentStore(
    (state) => [
      state.patient,
      state.doctor,
      state.serviceType,
      state.appointmentType,
      state.setData,
      state.resetData,
    ]
  );

  const handleTimeClick = (time: TimePickerDateTime) => {
    setSelectedTime(time);
    bookingFlowStore.updateAppointmentData("selectedTime", time);
    servicebookingFlowStore.updateAppointmentData("selectedTime", time);
    onChange(time);
  };

  const handleShowMore = () => setShowAll(true);

  const hasAnyMore = times.some(col => col.length > 5);

  return (
    <>
      <Stack direction="row" style={$timesContainerStyle}>
        {times.map((timeRow, outerIndex) => {
          const visibleSlots = showAll ? timeRow : timeRow.slice(0, 5);

          return (
            <Stack
              key={`times-${outerIndex}`}
              style={{
                ...getTimesStyle(),
                ...(outerIndex % 2 !== 0 && {
                  backgroundColor: colors.palette.neutral200,
                }),
              }}
            >
              {visibleSlots.map((obj, innerIndex) => (
                <Button
                  key={`time-${innerIndex}`}
                  onClick={() => handleTimeClick(obj)}
                  style={getTimeContainerStyles(obj)}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  variant="ghost"
                  padding={1}
                  isDisabled={!obj.formattedTime}
                  _hover={{ bg: buttonBgColor, color: "white" }}
                >
                  {obj.formattedTime}
                </Button>
              ))}
            </Stack>
          );
        })}
      </Stack>

      {!showAll && hasAnyMore && (
        <Box pt={3} textAlign="center">
          <Button
            size="sm"
            border={'2px'}
            borderRadius={'full'}
            variant="outline"
            borderColor={buttonBgColor}
            color={buttonBgColor}
            fontWeight="bold"
            onClick={handleShowMore}
          >
            More
          </Button>
        </Box>
      )}
    </>
  );
});

// Static styles that don't depend on observables
const $timesContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
};

const $timeContainerStyle: React.CSSProperties = {
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  borderColor: colors.border,
  borderBottomWidth: 1,
  borderWidth: 1,
  height: 45,
  borderRadius: 0,
};